import {
  Alert,
  Box,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material'
import type { SelectChangeEvent } from '@mui/material'
import type { TFunction } from 'i18next'
import type { FieldError } from 'react-hook-form'
import { FieldValues } from 'react-hook-form'
import { DayOfWeekPicker } from 'shared/components'
import { FieldWrapper } from '../ScheduleFields.style'
import {
  fieldCaptionLabelSx,
  radioOptionIndentSx,
  scheduleFrequencyOptionBodySx,
} from '../ScheduleFields.utils'
import { DaySelect } from '../DaySelect'
import { ScheduleOptions, type ScheduleOptionsProps } from '../ScheduleOptions'
import type { ScheduleTimePickersProps } from '../ScheduleTimePickers'
import { TimeZoneSelect } from '../TimeZoneSelect'
import type {
  ScheduleFieldNames,
  ScheduleFieldsProps,
  ScheduleTranslations,
  ScheduleValueConfig,
} from '../ScheduleFields.utils'

export type SectionCyclicAccessProps<T extends FieldValues> = {
  disabled: boolean
  cyclicValue: string
  label: string
  labels: ScheduleTranslations
  isActive: boolean
  unsupportedRecurringPattern?: boolean
  unsupportedAlertSeverity: 'error' | 'warning'
  embeddedTimeZoneControl?: ScheduleFieldsProps<T>['embeddedTimeZoneControl']
  control: ScheduleFieldsProps<T>['control']
  fieldNames: ScheduleFieldNames
  valueConfig: ScheduleValueConfig
  frequency: string
  t: TFunction
  radioGroupIds: { frequency?: string }
  onFrequencyChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  selectedDaysOfWeek: string[]
  weeklyRangeFrom: string
  weeklyRangeTo: string
  onWeeklyRangeFromChange: (event: SelectChangeEvent<string>) => void
  onWeeklyRangeToChange: (event: SelectChangeEvent<string>) => void
  onSelectedDaysChange: (days: string[]) => void
  daysOfWeekPickerError: FieldError[] | undefined
  shouldShowErrors: boolean
  clearErrors: ScheduleFieldsProps<T>['clearErrors']
  getFieldError: (fieldKey: string) => string | undefined
  timePickersProps: ScheduleTimePickersProps<T>
  scheduleOptionsProps: Omit<
    ScheduleOptionsProps<T>,
    keyof ScheduleTimePickersProps<T> | 'shouldShow'
  >
  weekRangeToFooterCaption?: string | null
}

export function SectionCyclicAccess<T extends FieldValues>({
  disabled,
  cyclicValue,
  label,
  labels,
  isActive,
  unsupportedRecurringPattern,
  unsupportedAlertSeverity,
  embeddedTimeZoneControl,
  control,
  fieldNames,
  valueConfig,
  frequency,
  t,
  radioGroupIds,
  onFrequencyChange,
  selectedDaysOfWeek,
  weeklyRangeFrom,
  weeklyRangeTo,
  onWeeklyRangeFromChange,
  onWeeklyRangeToChange,
  onSelectedDaysChange,
  daysOfWeekPickerError,
  shouldShowErrors,
  clearErrors,
  getFieldError,
  timePickersProps,
  scheduleOptionsProps,
  weekRangeToFooterCaption,
}: SectionCyclicAccessProps<T>) {
  const frequencyGroupId = radioGroupIds.frequency ?? 'schedule-frequency'

  return (
    <Box sx={{ width: '100%' }}>
      <FormControlLabel
        value={cyclicValue}
        control={<Radio disabled={disabled} />}
        label={label}
      />
      {isActive ? (
        <Box sx={radioOptionIndentSx}>
          <FieldWrapper container>
            {unsupportedRecurringPattern ? (
              <Grid item xs={12} sx={{ mb: 2 }}>
                <Alert severity={unsupportedAlertSeverity}>
                  {labels.unsupportedRecurringPattern}
                </Alert>
              </Grid>
            ) : null}
            {embeddedTimeZoneControl ? (
              <TimeZoneSelect
                control={control}
                fieldNames={fieldNames}
                timeZoneControl={embeddedTimeZoneControl}
                disabled={disabled}
                shouldShowErrors={shouldShowErrors}
                clearErrors={clearErrors}
                getFieldError={getFieldError}
              />
            ) : null}
            <Grid item container justifyContent="flex-start">
              <Grid item xs={12}>
                <InputLabel
                  shrink
                  htmlFor={frequencyGroupId}
                  sx={fieldCaptionLabelSx}
                >
                  {labels.repeat}
                </InputLabel>
                <RadioGroup
                  aria-label="schedule-frequency"
                  id={frequencyGroupId}
                  name={fieldNames.frequency}
                  value={frequency}
                  onChange={onFrequencyChange}
                  sx={{ gap: 0 }}
                >
                  <Box sx={{ width: '100%', mb: 0.25 }}>
                    <FormControlLabel
                      value={valueConfig.freqDaily}
                      control={<Radio disabled={disabled} />}
                      label={labels.frequencyDaily}
                    />
                    {frequency === valueConfig.freqDaily ? (
                      <Stack spacing={2} sx={scheduleFrequencyOptionBodySx}>
                        <ScheduleOptions
                          {...timePickersProps}
                          {...scheduleOptionsProps}
                        />
                      </Stack>
                    ) : null}
                  </Box>
                  <Box sx={{ width: '100%', mb: 0.25 }}>
                    <FormControlLabel
                      value={valueConfig.freqWeekly}
                      control={<Radio disabled={disabled} />}
                      label={labels.frequencyWeekly}
                    />
                    {frequency === valueConfig.freqWeekly ? (
                      <Stack spacing={2} sx={scheduleFrequencyOptionBodySx}>
                        <Stack spacing={2} sx={{ width: '100%' }}>
                          <DaySelect
                            disabled={disabled}
                            fieldId="weekly-range-from"
                            caption={labels.weekRangeFrom}
                            value={weeklyRangeFrom}
                            onChange={onWeeklyRangeFromChange}
                            t={t}
                          />
                          <DaySelect
                            disabled={disabled}
                            fieldId="weekly-range-to"
                            caption={labels.weekRangeTo}
                            value={weeklyRangeTo}
                            onChange={onWeeklyRangeToChange}
                            t={t}
                            footerCaption={weekRangeToFooterCaption}
                          />
                        </Stack>
                        <ScheduleOptions
                          {...timePickersProps}
                          {...scheduleOptionsProps}
                        />
                      </Stack>
                    ) : null}
                  </Box>
                  <Box sx={{ width: '100%' }}>
                    <FormControlLabel
                      value={valueConfig.freqSelectedDays}
                      control={<Radio disabled={disabled} />}
                      label={labels.frequencySelectedDays}
                    />
                    {frequency === valueConfig.freqSelectedDays ? (
                      <Stack spacing={2} sx={scheduleFrequencyOptionBodySx}>
                        <DayOfWeekPicker
                          disabled={disabled}
                          currentDays={selectedDaysOfWeek}
                          onChange={onSelectedDaysChange}
                          error={daysOfWeekPickerError}
                        />
                        <ScheduleOptions
                          {...timePickersProps}
                          {...scheduleOptionsProps}
                        />
                      </Stack>
                    ) : null}
                  </Box>
                </RadioGroup>
              </Grid>
            </Grid>
          </FieldWrapper>
        </Box>
      ) : null}
    </Box>
  )
}
